﻿/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Paridad visual/funcional con:
 * documentacion/design/prototypes/negocio/prototypes/multi-org-owner-panel-v1.html (caso B hub panel)
 */
import React, { useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  Easing,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { OwnerPanelPeriodTabs } from '../components/OwnerPanelPeriodTabs';
import { OwnerPanelLoadingSkeleton } from '../components/OwnerPanelLoadingSkeleton';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { formatSalesDelta, formatRefreshedAgo, type SalesDelta } from '../application/dateRanges';
import type { BranchPanelSnapshot } from '../domain/types';
import { formatMoney } from '../../shared/format/public-api';
import {
  useOwnerBranchesPanelScreen,
  type OwnerPanelEntryTarget,
} from './useOwnerBranchesPanelScreen';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useOrganizationLogoNudge } from '../../onboarding/application/useOrganizationLogoNudge';
import { OrganizationIdentityNudgeCard } from '../../onboarding/components/OrganizationIdentityNudgeCard';
import { navigateToOrganizationIdentity } from '../../settings/navigation/openOrganizationIdentity';


function deltaColor(delta: SalesDelta | null): string {
  if (!delta) return colors.textSecondary;
  if (delta.tone === 'up' || delta.tone === 'new') return colors.success;
  if (delta.tone === 'down') return colors.danger;
  return colors.textSecondary;
}

type Props = {
  entryTarget?: OwnerPanelEntryTarget;
  navigationOverride?: NavigationProp<ParamListBase>;
  testID?: string;
};

function CashPill({ open }: { open: boolean | null }) {
  if (open == null) {
    return null;
  }
  if (open) {
    return (
      <View style={[styles.pill, styles.pillOk]}>
        <Text style={styles.pillOkText}>Caja abierta</Text>
      </View>
    );
  }
  // #1468: caja cerrada es un estado operativo (fuera de horario), no una
  // alerta — pill neutra, sin color de warning.
  return (
    <View style={[styles.pill, styles.pillNeutral]}>
      <Text style={styles.pillNeutralText}>Caja cerrada</Text>
    </View>
  );
}

function BranchCard({
  snap,
  priorLabel,
  isActive,
  showPendingOrders,
  showLowStock,
  onPress,
}: {
  snap: BranchPanelSnapshot;
  priorLabel: string;
  isActive: boolean;
  showPendingOrders: boolean;
  showLowStock: boolean;
  onPress: () => void;
}) {
  const delta = formatSalesDelta(snap.salesTotal ?? 0, snap.priorSalesTotal ?? 0);
  const salesLabel = `${snap.salesCount ?? 0} venta${(snap.salesCount ?? 0) === 1 ? '' : 's'}`;
  // #1468: stat tiles solo cuando hay algo que mirar (> 0). Tres ceros por
  // sucursal multiplican ruido sin información.
  const pendingOrders = snap.pendingOrders ?? 0;
  const lowStock = snap.lowStockCount ?? 0;
  const agendaToday = snap.appointmentsToday ?? 0;
  const showAgenda = snap.appointmentsToday != null && agendaToday > 0;
  const hasStats =
    (showPendingOrders && pendingOrders > 0) ||
    (showLowStock && lowStock > 0) ||
    showAgenda;

  return (
    <TouchableOpacity
      style={[styles.branchCard, isActive && styles.branchCardActive]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Ir a ${snap.branch.name}`}
      testID={`owner-panel.branch.${snap.branch.id}`}
    >
      <View style={styles.branchTop}>
        <View style={styles.branchTitleWrap}>
          <Text style={styles.branchName}>{snap.branch.name}</Text>
          <Text style={styles.branchMeta}>
            <Text style={styles.branchMetaMuted}>{salesLabel}</Text>
            {delta ? (
              <Text style={{ color: deltaColor(delta) }}>
                {` · ${delta.text} ${priorLabel}`}
              </Text>
            ) : null}
          </Text>
        </View>
        <View style={styles.branchRight}>
          <Text style={styles.branchSales}>{formatMoney(snap.salesTotal ?? 0)}</Text>
          <CashPill open={snap.cashOpen} />
        </View>
      </View>
      {hasStats ? (
        <View style={styles.stats}>
          {showPendingOrders && pendingOrders > 0 ? (
            <View style={styles.stat}>
              <Text style={[styles.statValue, styles.statValueWarn]}>{pendingOrders}</Text>
              <Text style={styles.statLabel}>pedidos pend.</Text>
            </View>
          ) : null}
          {showLowStock && lowStock > 0 ? (
            <View style={styles.stat}>
              <Text style={[styles.statValue, styles.statValueWarn]}>{lowStock}</Text>
              <Text style={styles.statLabel}>stock bajo</Text>
            </View>
          ) : null}
          {showAgenda ? (
            <View style={styles.stat}>
              <Text style={styles.statValue}>{agendaToday}</Text>
              <Text style={styles.statLabel}>agenda hoy</Text>
            </View>
          ) : null}
        </View>
      ) : null}
    </TouchableOpacity>
  );
}

export function OwnerBranchesPanelScreen(props: Props = {}) {
  const {
    entryTarget = 'shell',
    navigationOverride,
    testID = 'owner-panel.scroll',
  } = props;
  const defaultNavigation = useNavigation<NavigationProp<ParamListBase>>();
  const navigation = navigationOverride ?? defaultNavigation;
  const vm = useOwnerBranchesPanelScreen(navigation, { entryTarget });
  const { width } = useWindowDimensions();
  const wide = width >= CONTENT_WIDE_BREAKPOINT;
  const userId = useAuthStore((s) => s.session?.user?.id);
  const { entitlements } = usePlanEntitlements();
  const logoNudge = useOrganizationLogoNudge(userId, entitlements?.organization_id);

  const totalDelta = useMemo(
    () =>
      vm.summary
        ? formatSalesDelta(vm.summary.totalSales, vm.summary.totalPriorSales)
        : null,
    [vm.summary],
  );

  const branchSectionLabel = 'Por sucursal · elegí un local';

  const contentOpacity = useRef(new Animated.Value(0)).current;
  const hasAnimatedIn = useRef(false);

  useEffect(() => {
    if (!vm.summary) {
      hasAnimatedIn.current = false;
      contentOpacity.setValue(0);
      return;
    }
    if (hasAnimatedIn.current) {
      contentOpacity.setValue(1);
      return;
    }
    hasAnimatedIn.current = true;
    Animated.timing(contentOpacity, {
      toValue: 1,
      duration: 240,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [vm.summary, contentOpacity]);

  const showInitialSkeleton = vm.loading && !vm.summary;

  if (vm.accessResolved && !vm.canAccessOwnerPanel) {
    return (
      <ShellContentFrame>
        <View style={styles.denied} testID="owner-panel.access-denied">
          <Text style={styles.kicker}>Panel del dueño</Text>
          <Text style={styles.deniedTitle}>No tenés acceso a este panel.</Text>
          <Text style={styles.empty}>Este resumen está disponible para dueños con dos o más sucursales activas.</Text>
        </View>
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame>
      <ScrollView
        contentContainerStyle={[styles.scroll, wide && styles.scrollWide]}
        refreshControl={<RefreshControl refreshing={vm.refreshing} onRefresh={vm.onRefresh} />}
        testID={testID}
      >
        <View style={[styles.topBar, wide && styles.topBarWide]}>
          <View style={styles.kickerRow}>
            {entryTarget === 'shell' ? (
              <TouchableOpacity
                onPress={() => navigation.goBack()}
                style={styles.backBtn}
                accessibilityRole="button"
                accessibilityLabel="Volver"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="arrow-back" size={22} color={colors.textPrimary} />
              </TouchableOpacity>
            ) : null}
            <Text style={styles.kicker}>
              {entryTarget === 'shell' ? 'Panel del dueño' : 'Hub · Panel del dueño'}
            </Text>
          </View>
          <OwnerPanelPeriodTabs
            period={vm.period}
            onPeriodChange={vm.setPeriod}
            layout={wide ? 'compact' : 'full'}
          />
        </View>

        {entryTarget === 'post_login_hub' && logoNudge.showNudgeCard ? (
          <OrganizationIdentityNudgeCard
            onUploadPress={() =>
              navigateToOrganizationIdentity(navigation, 'hub', { focusSection: 'logo' })
            }
            onDismiss={() => void logoNudge.dismissCard()}
            dismissing={logoNudge.dismissing}
          />
        ) : null}

        {showInitialSkeleton ? (
          <OwnerPanelLoadingSkeleton
            branches={vm.branches}
            organizationName={vm.organizationName}
            wide={wide}
            showPendingOrders={vm.publicStoreOn}
            showLowStock={vm.hasInventory}
          />
        ) : null}

        {vm.summary ? (
          <Animated.View style={{ opacity: contentOpacity, gap: spacing.sm }}>
            <View style={styles.totalCard} testID="owner-panel.total">
              <Text style={styles.totalLabel}>Total {vm.organizationName}</Text>
              <Text style={[styles.totalValue, wide && styles.totalValueWide]}>
                {formatMoney(vm.summary.totalSales)}
              </Text>
              <Text style={[styles.totalDelta, { color: deltaColor(totalDelta) }]}>
                {totalDelta
                  ? `${totalDelta.text} ${vm.summary.priorLabel}`
                  : `${vm.summary.totalSalesCount} venta${vm.summary.totalSalesCount === 1 ? '' : 's'}`}
                {totalDelta
                  ? ` · ${vm.summary.branches.length} sucursal${vm.summary.branches.length === 1 ? '' : 'es'}`
                  : ''}
              </Text>
              {vm.lastRefreshedAt ? (
                <Text style={styles.refreshedAt} testID="owner-panel.refreshed-at">
                  {formatRefreshedAgo(vm.lastRefreshedAt)}
                </Text>
              ) : null}
            </View>

            <Text style={styles.sectionLabel}>{branchSectionLabel}</Text>
            <View style={styles.branchGrid} testID="owner-panel.branches">
              {vm.summary.branches.map((snap) => (
                <View
                  key={snap.branch.id}
                  style={wide ? styles.branchSlotWide : styles.branchSlotFull}
                >
                  <BranchCard
                    snap={snap}
                    priorLabel={vm.summary!.priorLabel}
                    isActive={snap.branch.id === vm.currentBranchId}
                    showPendingOrders={vm.publicStoreOn}
                    showLowStock={vm.hasInventory}
                    onPress={() => void vm.goToBranch(snap.branch.id)}
                  />
                </View>
              ))}
            </View>

            <Text style={styles.sectionLabel}>Alertas</Text>
            <View style={styles.alerts} testID="owner-panel.alerts">
              {vm.summary.alerts.length === 0 ? (
                <View style={styles.alertRow}>
                  <Text style={styles.alertText}>Sin alertas urgentes hoy.</Text>
                </View>
              ) : (
                vm.summary.alerts.map((alert) => (
                  <TouchableOpacity
                    key={alert.id}
                    style={[styles.alertRow, alert.severity === 'warn' && styles.alertRowBad]}
                    onPress={() => void vm.goToBranch(alert.branchId)}
                    accessibilityRole="button"
                    accessibilityLabel={`${alert.branchName}: ${alert.message}`}
                  >
                    <Text style={styles.alertText}>
                      <Text style={styles.alertBranch}>{alert.branchName}: </Text>
                      {alert.message}
                    </Text>
                    <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                  </TouchableOpacity>
                ))
              )}
            </View>

            <TouchableOpacity
              style={styles.reportsCta}
              onPress={vm.openReports}
              accessibilityRole="button"
              testID="owner-panel.reports"
            >
              <Ionicons name="bar-chart-outline" size={18} color={colors.accent} />
              <Text style={styles.reportsCtaText}>Ver informes</Text>
              <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
            </TouchableOpacity>
          </Animated.View>
        ) : !vm.loading ? (
          <Text style={styles.empty}>No pudimos cargar el panel. Probá de nuevo.</Text>
        ) : null}
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  denied: {
    flex: 1,
    padding: spacing.xl,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
  },
  deniedTitle: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 18,
    color: colors.textPrimary,
    textAlign: 'center',
  },
  scroll: {
    padding: spacing.md,
    paddingBottom: spacing.xxl,
    gap: spacing.sm,
  },
  scrollWide: {
    maxWidth: 1080,
    width: '100%',
    alignSelf: 'center',
  },
  topBar: {
    gap: spacing.sm,
  },
  topBarWide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  kicker: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.accent,
    flexShrink: 1,
  },
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
    minWidth: 0,
  },
  backBtn: {
    flexShrink: 0,
    marginLeft: -spacing.xs,
  },
  totalCard: {
    ...ticketSurfaces.paperSheet,
    padding: 14,
  },
  totalLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: colors.textMuted,
  },
  totalValue: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 26,
    color: colors.textPrimary,
    marginTop: 4,
    fontVariant: ['tabular-nums'],
  },
  totalValueWide: {
    fontSize: 30,
  },
  totalDelta: {
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    fontSize: 13,
    marginTop: 4,
  },
  refreshedAt: {
    fontFamily: ticketFonts.sans,
    fontSize: 11.5,
    color: colors.textMuted,
    marginTop: 6,
  },
  sectionLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  branchGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'stretch',
  },
  branchSlotFull: {
    width: '100%',
  },
  branchSlotWide: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '30%',
    minWidth: 240,
  },
  branchCard: {
    ...ticketSurfaces.paperSheet,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    gap: 10,
    height: '100%',
  },
  branchCardActive: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  branchTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  branchTitleWrap: { flex: 1, minWidth: 0, gap: 2 },
  branchName: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: colors.textPrimary,
  },
  branchMeta: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
  },
  branchMetaMuted: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
  },
  branchRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  branchSales: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 16,
    color: colors.textPrimary,
    fontVariant: ['tabular-nums'],
  },
  pill: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  pillOk: { backgroundColor: 'rgba(21, 128, 61, 0.10)' },
  pillOkText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    color: colors.success,
  },
  pillNeutral: { backgroundColor: 'rgba(28, 25, 23, 0.06)' },
  pillNeutralText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    color: colors.textSecondary,
  },
  stats: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 6,
  },
  stat: {
    flexGrow: 1,
    flexShrink: 1,
    backgroundColor: 'rgba(28, 25, 23, 0.03)',
    borderRadius: 10,
    padding: 8,
  },
  statValue: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 12.5,
    color: colors.textPrimary,
    marginBottom: 2,
    fontVariant: ['tabular-nums'],
  },
  statValueWarn: {
    color: colors.warning,
  },
  statLabel: {
    fontFamily: ticketFonts.sans,
    fontSize: 11.5,
    color: colors.textSecondary,
  },
  alerts: { gap: spacing.sm },
  alertRow: {
    ...ticketSurfaces.paperSheet,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderLeftWidth: 3,
    borderLeftColor: colors.warning,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  alertRowBad: {
    borderLeftColor: colors.danger,
  },
  alertText: {
    flex: 1,
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  alertBranch: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  reportsCta: {
    ...ticketSurfaces.paperSheet,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    marginTop: spacing.xs,
  },
  reportsCtaText: {
    flex: 1,
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: colors.accent,
  },
  empty: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.xl,
  },
});
